import { useEffect, useMemo, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { NewProjectModal, useGetApplicationsQuery } from '@features/apps';
import { useGetTenantsQuery } from '@features/tenants';
import { Add as AddIcon, Apps as AppsIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router';

import { PageContainer } from '@/components/layout';
import type { Application, Tenant } from '@/types';
import { logger } from '@/utils/logger';

/**
 * Helper function to generate user greeting
 */
const getUserGreeting = (user: { name?: string; email?: string } | undefined): string => {
  if (user?.name) return `, ${user.name}`;
  if (user?.email) return `, ${user.email.split('@')[0]}`;
  return '';
};

/**
 * Main dashboard showing applications list
 */
export const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<string>('all');
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);

  const { user } = useAuth0();
  const { data: tenants, isLoading: tenantsLoading } = useGetTenantsQuery();
  const {
    data: applications,
    isLoading: appsLoading,
    error: appsError,
  } = useGetApplicationsQuery();

  // Check if we received a tenantId from query parameters
  useEffect(() => {
    const tenantIdParam = searchParams.get('tenantId');
    if (tenantIdParam) {
      setSelectedTenant(tenantIdParam);
    }
  }, [searchParams]);

  // Sort tenants alphabetically by name
  const sortedTenants = useMemo(() => {
    if (!tenants) return [];
    return [...tenants].sort((a, b) => a.name.localeCompare(b.name));
  }, [tenants]);

  // Filter and sort applications by search, tenant, and group by tenantId
  const filteredApps = useMemo(() => {
    if (!applications) return [];

    const filtered = applications.filter((app) => {
      const matchesSearch =
        !searchQuery ||
        app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesTenant = selectedTenant === 'all' || app.tenantId === selectedTenant;

      return matchesSearch && matchesTenant && !app.archived;
    });

    // Sort by tenantId first (to group by organization), then by name
    return filtered.sort((a, b) => {
      if (a.tenantId !== b.tenantId) {
        return a.tenantId.localeCompare(b.tenantId);
      }
      return a.name.localeCompare(b.name);
    });
  }, [applications, searchQuery, selectedTenant]);

  const handleAppClick = (app: Application) => {
    navigate(`/a/${app.id}/dash`);
  };

  const handleNewProject = () => {
    setIsNewProjectModalOpen(true);
  };

  const renderAppsContent = () => {
    if (appsLoading || tenantsLoading) {
      return (
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (filteredApps.length === 0) {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            backgroundColor: 'background.paper',
            borderRadius: 2,
          }}
        >
          <AppsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {searchQuery || selectedTenant !== 'all' ? 'No projects found' : 'No projects yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchQuery || selectedTenant !== 'all'
              ? 'Try adjusting your search or filter.'
              : 'Create your first project to get started.'}
          </Typography>
          {!searchQuery && selectedTenant === 'all' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewProject}>
              Create Project
            </Button>
          )}
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {filteredApps.map((app) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={app.id}>
            <AppCard
              app={app}
              tenantName={tenants?.find((t: Tenant) => t.tenantId === app.tenantId)?.name}
              onClick={() => handleAppClick(app)}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  if (appsError) {
    // Log the full error for debugging
    logger.error('[Dashboard] Failed to load applications:', appsError);

    // Extract useful error details for display
    let errorDetails: string;
    if ('status' in appsError) {
      const dataString =
        typeof appsError.data === 'string'
          ? appsError.data.slice(0, 200)
          : JSON.stringify(appsError.data)?.slice(0, 200);
      errorDetails = `Status: ${appsError.status} — ${dataString}`;
    } else if ('message' in appsError) {
      errorDetails = (appsError as { message: string }).message;
    } else {
      errorDetails = 'Unknown error';
    }

    return (
      <PageContainer>
        <Alert severity="error">
          Failed to load applications. Please try again.
          {import.meta.env.DEV && (
            <Box component="pre" sx={{ mt: 1, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
              {errorDetails}
            </Box>
          )}
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Welcome header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Welcome{getUserGreeting(user)}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a project to get started or create a new one.
        </Typography>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          placeholder="Search projects..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            minWidth: 250,
            backgroundColor: 'background.paper',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
        />

        <FormControl
          size="small"
          sx={{
            minWidth: 200,
            backgroundColor: 'background.paper',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
            },
          }}
        >
          <InputLabel>Organization</InputLabel>
          <Select
            value={selectedTenant}
            label="Organization"
            onChange={(e) => {
              const newTenantId = e.target.value;
              setSelectedTenant(newTenantId);
              // Update URL with query parameter
              if (newTenantId === 'all') {
                navigate('/');
              } else {
                navigate(`/?tenantId=${newTenantId}`);
              }
            }}
          >
            <MenuItem value="all">All Organizations</MenuItem>
            {sortedTenants?.map((tenant: Tenant) => (
              <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                {tenant.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewProject}>
          New Project
        </Button>
      </Box>

      {/* Applications grid */}
      {renderAppsContent()}

      {/* New Project Modal */}
      <NewProjectModal
        open={isNewProjectModalOpen}
        onClose={() => setIsNewProjectModalOpen(false)}
      />
    </PageContainer>
  );
};

interface AppCardProps {
  app: Application;
  tenantName?: string;
  onClick: () => void;
}

// Generate consistent color for each tenant
const getTenantColor = (tenantId: string): string => {
  const colors = [
    '#00853E', // Primary green
    '#2196F3', // Blue
    '#9C27B0', // Purple
    '#FF9800', // Orange
    '#E91E63', // Pink
    '#00BCD4', // Cyan
    '#4CAF50', // Light green
    '#FF5722', // Deep orange
    '#673AB7', // Deep purple
    '#009688', // Teal
    '#FFC107', // Amber
    '#3F51B5', // Indigo
  ];

  // Generate consistent index from tenantId
  const hash = tenantId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const AppCard = ({ app, tenantName, onClick }: AppCardProps) => {
  // Check if description is meaningful (not empty, not "No description.")
  const hasDescription =
    app.description &&
    app.description.trim() !== '' &&
    app.description.toLowerCase() !== 'no description.' &&
    app.description.toLowerCase() !== 'no description';

  // Get consistent color for this tenant
  const tenantColor = getTenantColor(app.tenantId);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid',
        borderColor: '#e0e0e0',
        borderLeft: `4px solid ${tenantColor}`,
        backgroundColor: '#ffffff',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: '#bdbdbd',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <CardActionArea
        onClick={onClick}
        sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}
      >
        <CardContent sx={{ width: '100%', p: 2 }}>
          {/* Header with name and badge */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 1,
              gap: 1,
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              sx={{
                flex: 1,
                fontWeight: 600,
                fontSize: '1rem',
                lineHeight: 1.4,
                color: '#212121',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {app.name}
            </Typography>
            {app.integrationCount !== undefined && app.integrationCount > 0 && (
              <Chip
                size="small"
                label={app.integrationCount}
                sx={{
                  minWidth: 32,
                  height: 22,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  backgroundColor: '#f5f5f5',
                  color: 'text.secondary',
                  border: '1px solid #e0e0e0',
                }}
              />
            )}
          </Box>

          {/* Organization name */}
          {tenantName && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: hasDescription ? 1 : 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.7rem',
                }}
              >
                {tenantName}
              </Typography>
            </Box>
          )}

          {/* Description - only show if meaningful content exists */}
          {hasDescription && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.5,
              }}
            >
              {app.description}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
